// Service.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";


// ⚠️ Adjust this path to your actual background image asset.
// The SAME image is reused for both the hero and the "What We Offer" section.
import servicesBg from "../../assests/BG_IMAGE_URL.png";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const SERVICES = [
  {
    n: "01",
    title: "Web Development",
    desc: "Responsive, SEO-ready web platforms — from marketing sites to full customer portals — built on modern, maintainable stacks.",
    tags: ["React", "Next.js", "Node.js", "SEO"],
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <rect x="4" y="8" width="32" height="22" rx="3" stroke="currentColor" strokeWidth="2" />
        <path d="M14 34h12M20 30v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M11 18l5 5 9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    n: "02",
    title: "Mobile App Development",
    desc: "Cross-platform iOS & Android apps with native-like performance, built once and shipped everywhere.",
    tags: ["React Native", "Flutter", "iOS", "Android"],
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <rect x="12" y="4" width="16" height="32" rx="4" stroke="currentColor" strokeWidth="2" />
        <circle cx="20" cy="30" r="1.5" fill="currentColor" />
        <path d="M17 10h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    n: "03",
    title: "Cloud & DevOps",
    desc: "Managed cloud infrastructure, CI/CD pipelines and 24×7 monitored uptime so releases ship without downtime.",
    tags: ["AWS", "Docker", "CI/CD", "Monitoring"],
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <path d="M10 28a8 8 0 010-16 10 10 0 0119.2 4A6 6 0 0130 28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M20 32v-10M17 25l3-3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    n: "04",
    title: "AI & Automation",
    desc: "Applied machine learning and business-process automation that removes repetitive manual work from real operations.",
    tags: ["ML Pipelines", "RPA", "Chatbots", "Data Automation"],
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="4" stroke="currentColor" strokeWidth="2" />
        <path d="M20 6v4M20 30v4M6 20h4M30 20h4M9.8 9.8l2.8 2.8M27.4 27.4l2.8 2.8M9.8 30.2l2.8-2.8M27.4 12.6l2.8-2.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    n: "05",
    title: "UI/UX Design",
    desc: "Pixel-perfect interfaces backed by real user research — designed to convert, not just look polished.",
    tags: ["Figma", "Prototyping", "User Research", "Design Systems"],
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="2" />
        <circle cx="20" cy="20" r="5" stroke="currentColor" strokeWidth="2" />
        <path d="M20 6v4M20 30v4M6 20h4M30 20h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    n: "06",
    title: "Digital Marketing",
    desc: "SEO, paid campaigns and social media strategy that turn traffic into qualified leads — measured against real business KPIs, not vanity metrics.",
    tags: ["SEO", "Google Ads", "Social Media", "Analytics"],
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <path d="M6 24l8-9 7 6 11-13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M26 8h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 30h28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    n: "07",
    title: "IT Consulting & Training",
    desc: "Technical advisory and structured, job-ready upskilling programs taught by the same engineers who ship client work.",
    tags: ["Consulting", "Bootcamps", "Mentorship", "Placements"],
    icon: (
      <svg viewBox="0 0 40 40" fill="none">
        <path d="M4 32V8a2 2 0 012-2h16l8 8v18a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" />
        <path d="M12 20h14M12 26h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

const PROCESS = [
  { n: "01", title: "Discover", desc: "We scope the problem, constraints and success metrics before writing a single line of code." },
  { n: "02", title: "Design", desc: "Architecture and interface decisions are mapped out and reviewed with you before build begins." },
  { n: "03", title: "Develop", desc: "Agile sprints with visible progress — you see working software every week, not just at the end." },
  { n: "04", title: "Deploy & Support", desc: "Launch with monitoring in place, and continued support after go-live." },
];

const INDUSTRIES = ["Healthcare", "Retail", "Education", "Finance", "Manufacturing", "Logistics"];

// How many cards a single "next/prev" click advances by.
const CARDS_PER_STEP = 2;

export default function Service() {
  const navigate = useNavigate();
  const [heroRef, heroIn] = useInView(0.1);
  const [gridRef, gridIn] = useInView(0.08);
  const [processRef] = useInView(0.1);
  const [ctaRef, ctaIn] = useInView(0.2);

  // Shared background image style — reused by the hero and the showcase section
  const bgStyle = { "--hero-bg": `url(${servicesBg})` };

  // ── Scroll-driven "train" progress for the How We Work track ──
  const railWrapRef = useRef(null);
  const [trainProgress, setTrainProgress] = useState(0);

  useEffect(() => {
    let raf = null;

    const measure = () => {
      raf = null;
      const el = railWrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = rect.height + vh * 0.6;
      const covered = vh * 0.75 - rect.top;
      const p = Math.min(1, Math.max(0, covered / total));
      setTrainProgress(p);
    };

    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const activeStop = Math.round(trainProgress * (PROCESS.length - 1));

  // ── Services carousel: continuous auto-scroll + arrows/dots/drag ──
  // The track renders SERVICES twice back-to-back. Auto-scroll moves the
  // offset continuously; once it passes the width of ONE set, we subtract
  // that width so it wraps seamlessly (the second copy is identical, so the
  // wrap is invisible — the classic infinite-marquee trick).
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const [singleWidth, setSingleWidth] = useState(0);
  const dotCount = Math.max(1, Math.ceil(SERVICES.length / CARDS_PER_STEP));

  const recalcSingleWidth = useCallback(() => {
    const tr = trackRef.current;
    if (!tr) return;
    const w = tr.scrollWidth / 2; // track holds two copies of SERVICES
    setSingleWidth(w);
    setOffset((o) => (w > 0 ? o % w : 0));
  }, []);

  useEffect(() => {
    recalcSingleWidth();
    window.addEventListener("resize", recalcSingleWidth);
    return () => window.removeEventListener("resize", recalcSingleWidth);
  }, [recalcSingleWidth]);

  const stepDistance = () => {
    const tr = trackRef.current;
    if (!tr) return 0;
    const firstCard = tr.querySelector(".sv-mcard");
    if (!firstCard) return 0;
    const gap = parseFloat(getComputedStyle(tr).columnGap || getComputedStyle(tr).gap || "0");
    return (firstCard.getBoundingClientRect().width + gap) * CARDS_PER_STEP;
  };

  const wrap = (v) => {
    if (singleWidth <= 0) return 0;
    let r = v % singleWidth;
    if (r < 0) r += singleWidth;
    return r;
  };

  const goPrev = () => { setOffset((o) => wrap(o - stepDistance())); pauseThenResume(); };
  const goNext = () => { setOffset((o) => wrap(o + stepDistance())); pauseThenResume(); };
  const goToDot = (i) => { setOffset(wrap((singleWidth * i) / dotCount)); pauseThenResume(); };

  const activeDot = singleWidth > 0
    ? Math.min(dotCount - 1, Math.floor((offset / singleWidth) * dotCount))
    : 0;

  // ── Autoplay: real continuous motion via requestAnimationFrame ──
  const AUTO_SCROLL_SPEED = 36; // pixels per second
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const resumeTimeoutRef = useRef(null);

  const pauseThenResume = () => {
    setIsPaused(true);
    setIsAutoScrolling(false);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 3500);
  };

  useEffect(() => {
    if (isPaused || singleWidth <= 0) return;
    setIsAutoScrolling(true);
    let raf = null;
    let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      setOffset((o) => wrap(o + AUTO_SCROLL_SPEED * dt));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [isPaused, singleWidth]);

  useEffect(() => () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  // ── Manual drag / swipe scrolling ──
  const dragRef = useRef({ dragging: false, startX: 0, startOffset: 0, moved: false });
  const [isDragging, setIsDragging] = useState(false);

  const onPointerDown = (e) => {
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    dragRef.current = { dragging: true, startX: x, startOffset: offset, moved: false };
    setIsDragging(true);
    setIsPaused(true);
    setIsAutoScrolling(false);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const delta = dragRef.current.startX - x;
    if (Math.abs(delta) > 4) dragRef.current.moved = true;
    setOffset(wrap(dragRef.current.startOffset + delta));
  };

  const endDrag = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    setIsDragging(false);
    pauseThenResume();
  };

  return (
    <div className="sv-page">

      {/* ── 1. HERO ── */}
      <section className={`sv-hero${heroIn ? " sv-in" : ""}`} ref={heroRef} style={bgStyle}>
        <div className="sv-hero__blob sv-hero__blob--1" aria-hidden="true" />
        <div className="sv-hero__blob sv-hero__blob--2" aria-hidden="true" />

        <div className="sv-hero__inner">
          <span className="sv-eyebrow sv-anim" style={{ animationDelay: "0.05s" }}>Our Services</span>
          <h1 className="sv-hero__heading sv-anim" style={{ animationDelay: "0.15s" }}>
            Technology services built<br />
            to <span className="sv-accent">scale with you.</span>
          </h1>
          <p className="sv-hero__sub sv-anim" style={{ animationDelay: "0.25s" }}>
            From web platforms to cloud infrastructure and applied AI — SkillSync Pro
            delivers end-to-end IT services for businesses that need reliable,
            production-ready software.
          </p>
          <div className="sv-hero__actions sv-anim" style={{ animationDelay: "0.35s" }}>
            <button className="sv-btn-primary" onClick={() => navigate("/contact")}>
              Get In Touch <span className="sv-btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── 2. SERVICES SHOWCASE — stable background image + manual carousel ── */}
      <section className="sv-showcase" ref={gridRef} style={bgStyle}>
        <div className="sv-showcase__head">
          <span className="sv-eyebrow sv-eyebrow--light">What We Offer</span>
          <h2 className="sv-heading sv-heading--light">Services built around your goals</h2>
        </div>

        <div
          className={`sv-marquee${gridIn ? " sv-in" : ""}${isDragging ? " sv-dragging" : ""}${isAutoScrolling ? " sv-autoscrolling" : ""}`}
          ref={viewportRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => { if (!dragRef.current.dragging) setIsPaused(false); }}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={endDrag}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={endDrag}
        >
          <div
            className="sv-marquee__track"
            ref={trackRef}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {[...SERVICES, ...SERVICES].map((s, i) => (
              <div key={`${s.n}-${i}`} className="sv-mcard">
                <div className="sv-mcard__body">
                  <h3 className="sv-mcard__title">{s.title}</h3>
                  <p className="sv-mcard__desc">{s.desc}</p>
                  <button
                    type="button"
                    className="sv-mcard__link"
                    onClick={() => { if (!dragRef.current.moved) navigate("/contact"); }}
                  >
                    <span className="sv-mcard__link-icon">→</span>
                    Read more
                  </button>
                </div>
                <div className="sv-mcard__icon">{s.icon}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={`sv-carousel__nav${gridIn ? " sv-in" : ""}`}>
          <button
            type="button"
            className="sv-carousel__arrow sv-carousel__arrow--prev"
            onClick={goPrev}
            aria-label="Previous services"
          >
            ←
          </button>

          <div className="sv-carousel__dots">
            {Array.from({ length: dotCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`sv-carousel__dot${i === activeDot ? " is-active" : ""}`}
                onClick={() => goToDot(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            className="sv-carousel__arrow sv-carousel__arrow--next"
            onClick={goNext}
            aria-label="Next services"
          >
            →
          </button>
        </div>
      </section>

      {/* ── 3. PROCESS (scroll-driven train) + INDUSTRIES ── */}
      <section className="sv-process" ref={processRef}>
        <div className="sv-section__head">
          <span className="sv-eyebrow">How We Work</span>
          <h2 className="sv-heading">A process built for transparency</h2>
        </div>

        <div className="sv-process__wrap" ref={railWrapRef}>
          <div className="sv-process__rail">
            <span className="sv-process__rail-track" />
            <span
              className="sv-process__rail-fill"
              style={{ height: `${trainProgress * 100}%` }}
            />
            {PROCESS.map((p, i) => (
              <span
                key={p.n}
                className={`sv-process__dot${i <= activeStop ? " is-active" : ""}`}
                style={{ top: `${(i / (PROCESS.length - 1)) * 100}%` }}
              />
            ))}
            <span
              className="sv-process__train"
              style={{ top: `${trainProgress * 100}%` }}
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" fill="none">
                <rect x="4" y="4" width="16" height="12" rx="3" stroke="currentColor" strokeWidth="1.8" />
                <path d="M4 11h16" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="8" cy="19" r="1.6" fill="currentColor" />
                <circle cx="16" cy="19" r="1.6" fill="currentColor" />
                <path d="M9 4V2h6v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
          </div>

          <div className="sv-process__list">
            {PROCESS.map((p, i) => (
              <div key={p.n} className={`sv-process__item${i <= activeStop ? " is-active" : ""}`}>
                <span className="sv-process__num">{p.n}</span>
                <h3 className="sv-process__title">{p.title}</h3>
                <p className="sv-process__desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="sv-industries">
          <span className="sv-eyebrow">Industries We Serve</span>
          <div className="sv-industries__list">
            {INDUSTRIES.map((ind) => (
              <span key={ind} className="sv-chip">{ind}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. CTA ── */}
      <section className={`sv-cta${ctaIn ? " sv-in" : ""}`} ref={ctaRef}>
        <div className="sv-cta__glow" aria-hidden="true" />
        <h2 className="sv-cta__heading">Ready to start your project?</h2>
        <p className="sv-cta__sub">
          Tell us what you're building — our team responds to every enquiry
          within one business day.
        </p>
        <div className="sv-cta__actions">
          <button className="sv-cta__btn" onClick={() => navigate("/contact")}>
            Contact Us <span className="sv-btn-arrow">→</span>
          </button>
          <span className="sv-cta__location">Nagercoil, Tamil Nadu, India</span>
        </div>
      </section>

    </div>
  );
}